/**
 * KPI考核 - 自定义Hook
 * 提取自 KPIAssessment.tsx，管理所有数据获取和状态
 */
import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import {
    memberPerformanceRecordService, MemberPerformanceRecord,
    performancePeriodService, PerformancePeriod,
    kpiDimensionService, KPIDimension,
    memberKPITargetService, MemberKPITarget,
    kpiConfigService
} from '../../../services/administrationService';
import { departmentAPI, Department, Member } from '../../../services/organizationAPI';

export interface UseKPIAssessmentReturn {
    // 数据
    records: MemberPerformanceRecord[];
    periods: PerformancePeriod[];
    departments: Department[];
    members: Member[];
    dimensions: KPIDimension[];
    targets: MemberKPITarget[];
    // 筛选
    selectedPeriod: number | undefined;
    selectedDept: number | undefined;
    selectedMember: number | undefined;
    setSelectedPeriod: (id: number | undefined) => void;
    setSelectedDept: (id: number | undefined) => void;
    setSelectedMember: (id: number | undefined) => void;
    // 录入
    modalVisible: boolean;
    recordingMember: Member | null;
    recordValues: Record<number, number>;
    saving: boolean;
    // 操作
    loading: boolean;
    loadRecords: () => Promise<void>;
    openRecordModal: (member: Member) => Promise<void>;
    setRecordValues: React.Dispatch<React.SetStateAction<Record<number, number>>>;
    handleSaveRecord: () => Promise<void>;
    setModalVisible: (v: boolean) => void;
}

export function useKPIAssessment(): UseKPIAssessmentReturn {
    // 数据状态
    const [records, setRecords] = useState<MemberPerformanceRecord[]>([]);
    const [periods, setPeriods] = useState<PerformancePeriod[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [members, setMembers] = useState<Member[]>([]);
    const [dimensions, setDimensions] = useState<KPIDimension[]>([]);
    const [targets, setTargets] = useState<MemberKPITarget[]>([]);

    // 筛选状态
    const [selectedPeriod, setSelectedPeriod] = useState<number>();
    const [selectedDept, setSelectedDept] = useState<number>();
    const [selectedMember, setSelectedMember] = useState<number>();

    // UI状态
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [recordingMember, setRecordingMember] = useState<Member | null>(null);
    const [recordValues, setRecordValues] = useState<Record<number, number>>({});
    const [saving, setSaving] = useState(false);

    // 加载筛选器
    const loadFilters = useCallback(async () => {
        try {
            const [pRes, dRes] = await Promise.all([
                performancePeriodService.list({}),
                departmentAPI.list()
            ]);
            setPeriods(pRes);
            const deptData = Array.isArray(dRes.data) ? dRes.data : (dRes.data as any).results || [];
            setDepartments(deptData);

            const active = pRes.find(p => p.status === 'active');
            if (active) setSelectedPeriod(active.id);
            else if (pRes.length > 0) setSelectedPeriod(pRes[0].id);

            if (deptData.length > 0) setSelectedDept(deptData[0].id);
        } catch {
            message.error('加载筛选条件失败');
        }
    }, []);

    // 加载成员
    const loadMembers = useCallback(async () => {
        if (!selectedDept) return;
        try {
            const res = await departmentAPI.getMembers(selectedDept);
            const data = Array.isArray(res.data) ? res.data : (res.data as any).results || [];
            setMembers(data);
        } catch { /* ignore */ }
    }, [selectedDept]);

    // 加载维度
    const loadDimensions = useCallback(async () => {
        if (!selectedDept) return;
        try {
            const [allDims, configs] = await Promise.all([
                kpiDimensionService.list({ is_active: true }),
                kpiConfigService.list({ department_id: selectedDept, is_active: true })
            ]);
            const activeDimIds = new Set(configs.map(c => c.dimension));
            setDimensions(allDims.filter(d => activeDimIds.has(d.id)));
        } catch { /* ignore */ }
    }, [selectedDept]);

    // 加载记录
    const loadRecords = useCallback(async () => {
        if (!selectedPeriod || !selectedDept) return;
        setLoading(true);
        try {
            const data = await memberPerformanceRecordService.list({
                period_id: selectedPeriod,
                department_id: selectedDept,
                member_id: selectedMember
            });
            setRecords(data);
        } catch {
            message.error('加载业绩记录失败');
        } finally {
            setLoading(false);
        }
    }, [selectedPeriod, selectedDept, selectedMember]);

    // 打开录入弹窗
    const openRecordModal = useCallback(async (member: Member) => {
        setRecordingMember(member);
        setRecordValues({});

        if (selectedPeriod) {
            try {
                const targetData = await memberKPITargetService.list({
                    period_id: selectedPeriod,
                    member_id: member.id
                });
                setTargets(targetData);

                const existingRecords = await memberPerformanceRecordService.list({
                    period_id: selectedPeriod,
                    member_id: member.id
                });
                const initVals: Record<number, number> = {};
                existingRecords.forEach(r => { initVals[r.dimension] = r.actual_value; });
                setRecordValues(initVals);
            } catch { /* ignore */ }
        }

        setModalVisible(true);
    }, [selectedPeriod]);

    // 保存记录
    const handleSaveRecord = useCallback(async () => {
        if (!recordingMember || !selectedPeriod) return;

        const recordList = dimensions.map(dim => ({
            dimension_id: dim.id,
            actual_value: recordValues[dim.id] || 0
        })).filter(r => r.actual_value !== undefined);

        if (recordList.length === 0) {
            message.warning('请输入至少一个维度的实际值');
            return;
        }

        setSaving(true);
        try {
            await memberPerformanceRecordService.batchRecord(selectedPeriod, recordingMember.id, recordList);
            message.success('业绩录入成功');
            setModalVisible(false);
            loadRecords();
        } catch {
            message.error('录入失败');
        } finally {
            setSaving(false);
        }
    }, [recordingMember, selectedPeriod, dimensions, recordValues, loadRecords]);

    // Effects
    useEffect(() => { loadFilters(); }, [loadFilters]);
    useEffect(() => { if (selectedDept) { loadMembers(); loadDimensions(); } }, [selectedDept, loadMembers, loadDimensions]);
    useEffect(() => { if (selectedPeriod && selectedDept) loadRecords(); }, [selectedPeriod, selectedDept, selectedMember, loadRecords]);

    return {
        records, periods, departments, members, dimensions, targets,
        selectedPeriod, selectedDept, selectedMember,
        setSelectedPeriod, setSelectedDept, setSelectedMember,
        modalVisible, recordingMember, recordValues, saving,
        loading, loadRecords, openRecordModal, setRecordValues, handleSaveRecord, setModalVisible
    };
}
